import React, { useEffect, useMemo, useState } from 'react';
import { Button } from 'components/Button';
import { EditOutlined, DownOutlined } from '@ant-design/icons';
import { Checkbox, Select, Tooltip, Spin } from 'antd';
import { connect } from 'react-redux';
import { getHolidays, saveHolidays } from 'actions/profileNew';
import moment from 'moment';
import { useLocation } from 'react-router-dom/cjs/react-router-dom.min';
import ConfirmLeaveEdit from './localComponent/confirmLeaveEdit';
import { fluid } from 'components/UserDashboardV2/utils/responsive';
import { Api } from 'services';
import './userProfileAttendanceDetailsV3.css';

const MONTH_NAMES = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
const WEEKDAY_NAMES = ["M", "T", "W", "T", "F", "S", "S"];
const weedays = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const currentYear = new Date().getFullYear();

// variant="v2" (users V2 profile) — month cards with a header strip, weeks
// starting Sunday, and these day colours. Default (V3) is unchanged.
const V2_WEEKDAY_NAMES = ["SUN", "MON", "TUE", "WED", "THU", "FRI", "SAT"];
// Day tile colours from the design (background / date text).
const V2_DAY_COLORS = {
    // working: { bg: "#00A56633", text: "#00482D" }, // #00A566 at 20% — working-day colour removed
    working: { bg: "transparent", text: "#191919" },
    holiday: { bg: "#FF414D33", text: "#DB0007" }, // #FF414D at 20%
    offday: { bg: "#F4F4F4", text: "#636363" },
    // Before the user joined — disabled like the attendance calendar's future dates.
    disabled: { bg: "transparent", text: "#AEAEAE" },
};
// Legend swatches (solid colours from the design).
const V2_LEGEND = [
    // { label: "Working day", color: "#CCEDE0" },
    { label: "Holiday", color: "#FFD9DB" },
    { label: "Day off", color: "#EAEAEA" },
];

const HolidaysCalendar = ({
    getHolidays,
    user,
    setPageLoader,
    holidays,
    saveHolidays,
    specificUserId,
    useId,
    storeWorkingDaysDetails,
    attendanceRequirement,
    variant,
    joinedDate,
}) => {
    const isV2 = variant === 'v2';
    const [selectedYear, setSelectedYear] = useState(new Date().getFullYear().toString());
    const [editable, setEditable] = useState(false);
    const [holidayList, setHolidayList] = useState([]);
    const [buttonLoader, setButtonLoader] = useState(false);
    const [loader, toggleLoader] = useState(false);
    const [confirmVisible, setConfirmVisible] = useState(false);

    const location = useLocation();

    const handleEditClick = () => {
        setEditable(true);
        toggleLoader(true);
        const params = {
            edit: true,
            geoFenceId: holidays?.currentLocation?.id,
            userId: useId,
            year: selectedYear,
        };
        const timer = setTimeout(() => {
            toggleLoader(false);
        }, 8000);
        getHolidays(params, () => {
            clearTimeout(timer);
            toggleLoader(false);
        });
    };

    const handleCancelClick = () => {
        setHolidayList(cloneHolidayList(initialHolidayList));
        setEditable(false);
        toggleLoader(false);
    };

    const handleYearChange = (year) => {
        setSelectedYear(year);
        if (useId) {
            const params = {
                edit: undefined,
                geoFenceId: holidays?.currentLocation?.id,
                userId: useId,
                year: year,
            };
            getHolidays(params);
        }
    };

    const effectiveHolidayList = holidays?.holidayList || [];
    const [initialHolidayList, setInitialHolidayList] = useState([]);

    const cloneHolidayList = (list) => {
        if (!Array.isArray(list)) return [];
        return list.map(m => ({
            ...m,
            data: Array.isArray(m?.data) ? m.data.map(d => ({ ...d })) : []
        }));
    };

    useEffect(() => {
        if (effectiveHolidayList && effectiveHolidayList.length > 0) {
            setHolidayList(cloneHolidayList(effectiveHolidayList));
            setInitialHolidayList(cloneHolidayList(effectiveHolidayList));
        } else {
            setHolidayList([]);
            setInitialHolidayList([]);
        }
    }, [effectiveHolidayList, editable]);

    const hasHolidaysChanged = useMemo(() => {
        if (!initialHolidayList?.length || !holidayList?.length) return false;
        for (let mIdx = 0; mIdx < holidayList.length; mIdx++) {
            const currentMonthData = holidayList[mIdx]?.data || [];
            const initialMonthData = initialHolidayList[mIdx]?.data || [];
            if (currentMonthData.length !== initialMonthData.length) return true;
            for (let dIdx = 0; dIdx < currentMonthData.length; dIdx++) {
                if (Boolean(currentMonthData[dIdx]?.selected) !== Boolean(initialMonthData[dIdx]?.selected)) {
                    return true;
                }
            }
        }
        return false;
    }, [holidayList, initialHolidayList]);

    const hasGeofence = Boolean(holidays?.currentLocation && (holidays.currentLocation.id || holidays.currentLocation.title));

    // V2: does the assigned location have any holidays this year? Checked with
    // the edit list (all of the location's holidays), fetched directly so the
    // visible calendar isn't replaced. null = not known yet.
    const [locationHasHolidays, setLocationHasHolidays] = useState(null);
    const currentLocationId = holidays?.currentLocation?.id;
    useEffect(() => {
        if (!isV2 || !useId || !currentLocationId) {
            setLocationHasHolidays(null);
            return undefined;
        }
        let cancelled = false;
        const params = { edit: true, geoFenceId: currentLocationId, userId: useId, year: currentYear.toString() };
        Api.get(`/attendancev2/profile/${useId}/holiday`).params(params).send((response) => {
            if (cancelled) return;
            const list = Array.isArray(response?.holidayList) ? response.holidayList : [];
            setLocationHasHolidays(list.some((m) => Array.isArray(m?.data) && m.data.length > 0));
        }, true);
        return () => { cancelled = true; };
    }, [isV2, useId, currentLocationId]);
    // EDIT only for the current year, and (V2) only when the location has holidays.
    const canEditHolidays = String(selectedYear) === String(currentYear)
        && (!isV2 || locationHasHolidays === true);

    // Active working days lookup from redux or default Monday-Friday
    const activeWorkingDays = useMemo(() => {
        if (Array.isArray(holidays?.workingDays) && holidays.workingDays.length > 0) {
            return holidays.workingDays.map(d => String(d).toLowerCase());
        }
        if (Array.isArray(storeWorkingDaysDetails) && storeWorkingDaysDetails.length > 0) {
            return storeWorkingDaysDetails.map(d => String(d).toLowerCase());
        }
        if (Array.isArray(attendanceRequirement?.workingDays) && attendanceRequirement.workingDays.length > 0) {
            return attendanceRequirement.workingDays.map(d => String(d).toLowerCase());
        }
        return ['monday', 'tuesday', 'wednesday', 'thursday', 'friday'];
    }, [holidays?.workingDays, storeWorkingDaysDetails, attendanceRequirement?.workingDays]);

    // Build the 12 months data matching the Attendance Tab calendar in StaffProfilePage.js
    const calendarMonths = useMemo(() => {
        const yr = parseInt(selectedYear, 10) || currentYear;
        const currentMoment = moment();

        return MONTH_NAMES.map((monthName, monthIndex) => {
            const daysInMonth = new Date(yr, monthIndex + 1, 0).getDate();
            // V3: Mon=0..Sun=6; V2: weeks start on Sunday (Sun=0..Sat=6).
            const firstWeekday = isV2
                ? new Date(yr, monthIndex, 1).getDay()
                : (new Date(yr, monthIndex, 1).getDay() + 6) % 7;
            const leadingEmptyDays = Array.from({ length: firstWeekday }, () => ({ empty: true }));

            const monthData = effectiveHolidayList?.find(m =>
                m?.month?.toLowerCase() === monthName?.toLowerCase() ||
                moment(m?.month, 'MMMM').month() === monthIndex
            );

            const days = [...leadingEmptyDays];

            for (let day = 1; day <= daysInMonth; day++) {
                const dayDate = new Date(yr, monthIndex, day);
                const dayMoment = moment(dayDate);
                const dateStr = dayMoment.format('YYYY-MM-DD');
                const dayOfWeek = dayDate.getDay(); // 0 = Sun, 6 = Sat
                const dayNameLower = dayMoment.format('dddd').toLowerCase();

                // Check if holiday in effectiveHolidayList
                const holidayMatch = monthData?.data?.find(h => {
                    const hDate = moment(h?.date).format('YYYY-MM-DD');
                    return hDate === dateStr && (h?.selected || h?.saved === 1 || !holidays?.edit);
                });

                const isHoliday = Boolean(holidayMatch);

                // Check if offday
                const isExplicitOffday = Boolean(
                    holidayMatch?.type?.toLowerCase() === 'offday' ||
                    holidayMatch?.isOffDay ||
                    (Array.isArray(holidays?.offDays) && holidays.offDays.includes(dateStr))
                );

                let isWeeklyOff = false;
                if (Array.isArray(activeWorkingDays) && activeWorkingDays.length > 0) {
                    isWeeklyOff = !activeWorkingDays.includes(dayNameLower);
                } else {
                    // Default Mon-Fri: Sunday and Saturday are weekly offs
                    isWeeklyOff = (dayOfWeek === 0 || dayOfWeek === 6);
                }

                const isOffday = !isHoliday && (isExplicitOffday || isWeeklyOff);
                const isToday = dayMoment.isSame(currentMoment, 'day');

                let status = 'working';
                let backgroundColor = '#F1F1FF'; // Soft green for normal/working days matching attendance calendar
                let textColor = '#000000';
                let tooltip = null;

                if (isHoliday) {
                    status = 'holiday';
                    backgroundColor = '#FFD7D8'; // Red pastel color matching attendance calendar
                    textColor = '#000000';
                    tooltip = holidayMatch?.title || 'Holiday';
                } else if (isOffday) {
                    status = 'offday';
                    backgroundColor = '#F5F5F5'; // Gray color matching attendance calendar
                    textColor = '#AEAEAE'; // Muted/disabled color for offdays
                    tooltip = isV2 ? 'Day off' : 'Offday';
                }
                // Working-day tooltip removed along with its colour.
                // else if (isV2) { tooltip = 'Working day'; }

                // V2: dates before the user joined are disabled — no holiday / day off / tooltip.
                if (isV2 && joinedDate && dayMoment.isBefore(moment(joinedDate), 'day')) {
                    status = 'disabled';
                    tooltip = null;
                }

                days.push({
                    day,
                    status,
                    backgroundColor,
                    textColor,
                    isToday,
                    tooltip,
                });
            }

            return {
                title: `${monthName} ${yr}`,
                monthName,
                year: yr,
                days,
            };
        });
    }, [selectedYear, effectiveHolidayList, holidays, activeWorkingDays, isV2, joinedDate]);

    const handleSaveClick = () => {
        if (!hasHolidaysChanged) return;
        setConfirmVisible(true);
    };

    const handleConfirmSave = () => {
        setConfirmVisible(false);
        setButtonLoader(true);
        const dates = [];
        holidayList.forEach(month =>
            dates?.push(...month?.data?.filter(holiday => holiday.selected === true)?.map(item => moment(item?.date).format("YYYY-MM-DD")))
        );

        const params = {
            userId: useId,
            edit: true,
            year: selectedYear,
            dates: dates
        };
        console.log(params);
        saveHolidays(params, () => setButtonLoader(false), () => { toggleLoader(false); setEditable(false); });
    };

    const handleHolidayCheck = (e, dateIndex, monthIndex) => {
        const updated = holidayList.map((month, mIdx) => {
            if (mIdx !== monthIndex) return month;
            const updatedData = month.data.map((day, dIdx) => {
                if (dIdx !== dateIndex) return day;
                return { ...day, selected: e.target.checked };
            });
            return { ...month, data: updatedData };
        });
        setHolidayList(updated);
    };

    const handleSelectAll = (e, index) => {
        const updatedHolidayList = holidayList.map((item, idx) => {
            if (idx === index) {
                const upcoming = (item?.data || []).filter(day => moment(day.date).isAfter(moment(), 'day'));
                const isAllSelected = upcoming.length > 0 && upcoming.every(day => day.selected);
                const nextChecked = !isAllSelected;

                const updatedData = (item?.data || []).map(day => {
                    const isUpcoming = moment(day.date).isAfter(moment(), 'day');
                    if (!isUpcoming) return day;
                    return {
                        ...day,
                        selected: nextChecked
                    };
                });
                return {
                    ...item,
                    data: updatedData
                };
            }
            return item;
        });
        setHolidayList(updatedHolidayList);
    };

    return (
        <div>
            {/* Sticky header within the page's own scroll container */}
            <div
                className='holiday-flex-space-between'
                style={{
                    position: 'sticky',
                    top: 0,
                    zIndex: 2,
                    backgroundColor: '#FFFFFF',
                    paddingTop: fluid(16),
                    paddingBottom: fluid(12),
                }}
            >
                <div className='sumary-text-style-v3' style={isV2
                    ? { margin: 0, fontFamily: "Roboto, sans-serif", fontSize: fluid(24), fontWeight: 500, color: "#191919" }
                    : { margin: 0, fontSize: fluid(26), fontWeight: 500 }}>
                    Holidays
                </div>

                <div style={{ display: 'flex', alignItems: 'center', gap: fluid(16) }}>
                    {!editable && (
                        /* Year change dropdown pill beside left of the EDIT button */
                        <div
                            id="holiday-year-select-wrapper"
                            style={{
                                position: 'relative',
                                display: 'inline-flex',
                                alignItems: 'center',
                                border: '1px solid #D9D9D9',
                                borderRadius: fluid(20),
                                height: fluid(35),
                                backgroundColor: '#FFFFFF',
                                boxSizing: 'border-box',
                            }}
                        >
                            <Select
                                value={selectedYear}
                                onChange={handleYearChange}
                                disabled={loader}
                                bordered={false}
                                dropdownMatchSelectWidth={false}
                                getPopupContainer={(triggerNode) => triggerNode?.parentElement || document.getElementById('holiday-year-select-wrapper') || document.body}
                                style={{ width: 85 }}
                                suffixIcon={<DownOutlined style={{ fontSize: 10, color: '#666666' }} />}
                            >
                                {[
                                    (currentYear - 1).toString(),
                                    currentYear.toString(),
                                    // Future year removed — holidays only up to the current year.
                                    // (currentYear + 1).toString(),
                                ].map(yr => (
                                    <Select.Option key={yr} value={yr}>
                                        <span style={{ fontFamily: 'Roboto, sans-serif', fontSize: fluid(16), fontWeight: 500, color: '#191919' }}>
                                            {yr}
                                        </span>
                                    </Select.Option>
                                ))}
                            </Select>
                        </div>
                    )}

                    {editable ? (
                        <div className='r-ac' style={{ display: 'flex', gap: 24 }}>
                            <Button
                                onClick={handleCancelClick}
                                disabled={loader || buttonLoader}
                                style={{
                                    backgroundColor: 'transparent',
                                    border: '2px solid #AEAEAE',
                                    borderRadius: 999,
                                    color: '#AEAEAE',
                                    padding: '4px 12px',
                                    height: 30,
                                    fontFamily: 'Roboto, sans-serif',
                                    fontWeight: 700,
                                    fontSize: 12,
                                    opacity: (loader || buttonLoader) ? 0.5 : 1,
                                    cursor: (loader || buttonLoader) ? 'not-allowed' : 'pointer',
                                }}
                            >
                                CANCEL
                            </Button>
                            <Button
                                type="primary"
                                disabled={loader || !hasHolidaysChanged || buttonLoader}
                                onClick={loader || !hasHolidaysChanged || buttonLoader ? undefined : handleSaveClick}
                                style={{
                                    backgroundColor: '#594099',
                                    border: '2px solid #594099',
                                    borderRadius: 999,
                                    color: '#FFFFFF',
                                    padding: '4px 12px',
                                    height: 30,
                                    fontFamily: 'Roboto, sans-serif',
                                    fontWeight: 700,
                                    fontSize: 12,
                                    opacity: (loader || !hasHolidaysChanged || buttonLoader) ? 0.5 : 1,
                                    cursor: (loader || !hasHolidaysChanged || buttonLoader) ? 'not-allowed' : 'pointer',
                                }}
                            >
                                SAVE
                            </Button>
                        </div>
                    ) : (
                        <div>
                            {/* Hidden for a past / future year, or when the location has no holidays. */}
                            {(hasGeofence && canEditHolidays && (user?.privilage?.includes('SuperAdmin') || user?.privilage?.includes('LeaveManager'))) ? (
                                <div
                                    className='edit-button-v3'
                                    onClick={handleEditClick}
                                    style={{
                                        opacity: loader ? 0.5 : 1,
                                        pointerEvents: loader ? 'none' : 'auto',
                                        ...(isV2 ? { color: '#1089FF', fontFamily: 'Roboto, sans-serif', fontSize: fluid(16), fontWeight: 700, display: 'inline-flex', alignItems: 'center', gap: fluid(6) } : {}),
                                    }}
                                >
                                    <EditOutlined /> EDIT
                                </div>
                            ) : null}
                        </div>
                    )}
                </div>
            </div>

            {editable && loader ? (
                /* Holidays Section Loader while fetching holiday API in edit mode only */
                <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 250, padding: '40px 0', width: '100%' }}>
                    <Spin size="large" />
                </div>
            ) : editable ? (
                /* Edit Mode: Holiday checklist toggle - 3 cards per row, vertical scroll only */
                <div style={{ paddingBottom: 24 }}>
                    {holidayList && holidayList.length > 0 ? (
                        holidayList.map((item, index) => {
                            if (!item?.data || item.data.length === 0) return null;
                            const upcomingDates = (item?.data || []).filter(day => moment(day.date).isAfter(moment(), 'day'));
                            const isAllSelected = upcomingDates.length > 0 && upcomingDates.every(day => day.selected);
                            const isSomeSelected = upcomingDates.some(day => day.selected) && !isAllSelected;

                            return (
                                <div key={index} style={{ width: "100%", marginBottom: 20 }}>
                                    <div className='holiday-flex-center-space-between' style={{ marginBottom: 12, marginTop: index === 0 ? 12 : 24 }}>
                                        <div style={{ fontSize: 16, fontWeight: 500, color: '#191919' }}>{item.month}</div>
                                        {upcomingDates.length > 1 && (
                                            <Checkbox
                                                className="holiday-select-all-checkbox"
                                                onChange={(e) => handleSelectAll(e, index)}
                                                checked={isAllSelected}
                                                indeterminate={isSomeSelected}
                                            >
                                                <span className='holiday-text-gray-small'> Select All</span>
                                            </Checkbox>
                                        )}
                                    </div>
                                    <div className='holiday-cards-grid'>
                                        {item.data.map((day, i) => {
                                            const isUpcoming = moment(day.date).isAfter(moment(), 'day');
                                            return (
                                                <div key={i} className='holiday-card-item'>
                                                    <Checkbox
                                                        checked={day.selected}
                                                        onChange={(e) => handleHolidayCheck(e, i, index)}
                                                        disabled={!isUpcoming}
                                                    />
                                                    <div className='holiday-custom-flex-box'>
                                                        <div style={{ minWidth: 32, textAlign: 'center' }}>
                                                            <div className='holiday-month-date'>
                                                                {weedays[moment(day.date).day()]}
                                                            </div>
                                                            <div
                                                                style={{
                                                                    color: isUpcoming ? "#594099" : "#AEAEAE",
                                                                    fontSize: 14,
                                                                    fontWeight: 600,
                                                                    textAlign: "center"
                                                                }}
                                                            >
                                                                {moment(day.date).format("DD")}
                                                            </div>
                                                        </div>
                                                        <div className='holiday-custom-border-right'></div>
                                                        <div className='m-l-10 holiday-title' title={day.title}>
                                                            {day.title}
                                                        </div>
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>
                            );
                        })
                    ) : (
                        <div style={{ padding: '30px 0', textAlign: 'center', color: '#AEAEAE', fontSize: 16 }}>
                            No holidays available for {selectedYear}
                        </div>
                    )}
                </div>
            ) : isV2 ? (
                /* View Mode (users V2): month cards, 4 per row, weeks from Sunday */
                <div>
                    <div style={{ display: "flex", gap: fluid(24), marginBottom: fluid(20), marginTop: fluid(4), flexWrap: "wrap", alignItems: "center" }}>
                        {V2_LEGEND.map((item) => (
                            <div key={item.label} style={{ display: "flex", alignItems: "center", gap: fluid(8) }}>
                                <div style={{ width: fluid(16), height: fluid(16), borderRadius: fluid(4), backgroundColor: item.color }} />
                                <span style={{ fontFamily: "Roboto, sans-serif", fontSize: fluid(15), color: "#636363" }}>{item.label}</span>
                            </div>
                        ))}
                    </div>

                    <div
                        style={{
                            display: "grid",
                            gridTemplateColumns: "repeat(4, minmax(0, 1fr))",
                            gap: fluid(24),
                            width: "100%",
                            boxSizing: "border-box",
                            paddingBottom: fluid(8),
                        }}
                    >
                        {calendarMonths.map((month, idx) => (
                            <div
                                key={idx}
                                style={{
                                    minWidth: 0,
                                    border: "1px solid #EDEDED",
                                    borderRadius: fluid(16),
                                    overflow: "hidden",
                                    backgroundColor: "#FFFFFF",
                                    boxShadow: "0 2px 8px rgba(0, 0, 0, 0.06)",
                                }}
                            >
                                <div style={{
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "space-between",
                                    padding: `${fluid(12)} ${fluid(16)}`,
                                    backgroundColor: "#EEEBFF",
                                }}>
                                    <span style={{ fontFamily: "Roboto, sans-serif", fontSize: fluid(16), fontWeight: 500, color: "#594099" }}>
                                        {month.monthName}
                                    </span>
                                    <span style={{ fontFamily: "Roboto, sans-serif", fontSize: fluid(15), color: "#636363" }}>
                                        {month.year}
                                    </span>
                                </div>
                                <div style={{ padding: `${fluid(14)} ${fluid(14)} ${fluid(16)}` }}>
                                    <div style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gap: fluid(4), marginBottom: fluid(10), justifyItems: "center" }}>
                                        {V2_WEEKDAY_NAMES.map((dayName) => (
                                            <span key={dayName} style={{ fontFamily: "Roboto, sans-serif", fontSize: fluid(13), fontWeight: 400, color: "#636363", textAlign: "center" }}>
                                                {dayName}
                                            </span>
                                        ))}
                                    </div>
                                    <div style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", rowGap: fluid(8), columnGap: fluid(6), justifyItems: "center" }}>
                                        {month.days.map((dayItem, dayIdx) => {
                                            if (dayItem.empty) {
                                                return <div key={dayIdx} style={{ width: "100%", maxWidth: fluid(46), aspectRatio: "1 / 1" }} />;
                                            }
                                            const colors = V2_DAY_COLORS[dayItem.status] || V2_DAY_COLORS.working;
                                            const cell = (
                                                <div
                                                    style={{
                                                        // Square tile filling its column (capped), so the
                                                        // gaps between dates stay small as in the design.
                                                        width: "100%",
                                                        maxWidth: fluid(46),
                                                        aspectRatio: "1 / 1",
                                                        borderRadius: fluid(8),
                                                        backgroundColor: colors.bg,
                                                        border: dayItem.isToday ? "1.5px solid #594099" : "none",
                                                        display: "flex",
                                                        alignItems: "center",
                                                        justifyContent: "center",
                                                        boxSizing: "border-box",
                                                        cursor: dayItem.tooltip ? "pointer" : "default",
                                                    }}
                                                >
                                                    <span style={{ display: "block", lineHeight: 1, fontFamily: "Roboto, sans-serif", fontSize: fluid(18), fontWeight: dayItem.isToday ? 600 : 400, color: colors.text }}>
                                                        {dayItem.day}
                                                    </span>
                                                </div>
                                            );
                                            return dayItem.tooltip ? (
                                                <Tooltip
                                                    key={dayIdx}
                                                    title={dayItem.tooltip}
                                                    getPopupContainer={(triggerNode) => triggerNode?.parentElement || document.body}
                                                >
                                                    {cell}
                                                </Tooltip>
                                            ) : (
                                                <React.Fragment key={dayIdx}>{cell}</React.Fragment>
                                            );
                                        })}
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            ) : (
                /* View Mode: 3 Months per Row Grid with NO horizontal scroll */
                <div>
                    {/* Legend Row */}
                    <div style={{ display: "flex", gap: fluid(24), marginBottom: fluid(24), marginTop: fluid(12), flexWrap: "wrap", alignItems: "center" }}>
                        {[
                            { label: "Working day", color: "#F1F1FF" },
                            { label: "Offday", color: "#F2F2F2" },
                            { label: "Holiday", color: "#FFD7D8" },
                        ].map((item, idx) => (
                            <div key={idx} style={{ display: "flex", alignItems: "center", gap: fluid(8) }}>
                                <div style={{ width: fluid(18), height: fluid(18), borderRadius: fluid(4), backgroundColor: item.color, boxSizing: 'border-box' }} />
                                <span style={{ fontFamily: "Roboto, sans-serif", fontSize: fluid(18), color: "#666666" }}>{item.label}</span>
                            </div>
                        ))}
                    </div>

                    {/* 3 Months per Row Grid (all 12 months wrap naturally in rows of 3) */}
                    <div
                        style={{
                            display: "grid",
                            gridTemplateColumns: "repeat(3, 1fr)",
                            gap: `${fluid(36)} ${fluid(32)}`,
                            width: "100%",
                            boxSizing: "border-box",
                            paddingBottom: fluid(32),
                        }}
                    >
                        {calendarMonths.map((month, idx) => (
                            <div key={idx} style={{ minWidth: 0, maxWidth: fluid(440) }}>
                                <span style={{ display: "block", fontFamily: "Roboto, sans-serif", fontSize: fluid(22), fontWeight: 500, color: "#191919", marginBottom: fluid(14) }}>
                                    {month.title}
                                </span>
                                {/* Days of week header */}
                                <div style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gap: fluid(4), marginBottom: fluid(10), justifyItems: "center" }}>
                                    {WEEKDAY_NAMES.map((dayName, dIdx) => (
                                        <span key={dIdx} style={{ fontFamily: "Roboto, sans-serif", fontSize: fluid(17), fontWeight: 400, color: "#AEAEAE", textAlign: "center" }}>
                                            {dayName}
                                        </span>
                                    ))}
                                </div>
                                {/* Day cells grid */}
                                <div style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gap: fluid(4) }}>
                                    {month.days.map((dayItem, dayIdx) => {
                                        if (dayItem.empty) {
                                            return <div key={dayIdx} style={{ aspectRatio: "1 / 1" }} />;
                                        }

                                        const cell = (
                                            <div
                                                style={{
                                                    aspectRatio: "1 / 1",
                                                    borderRadius: fluid(12),
                                                    backgroundColor: dayItem.backgroundColor,
                                                    border: dayItem.isToday ? "2px solid #594099" : "none",
                                                    display: "flex",
                                                    alignItems: "center",
                                                    justifyContent: "center",
                                                    boxSizing: "border-box",
                                                    cursor: dayItem.tooltip ? "pointer" : "default",
                                                    transition: "transform 0.1s ease",
                                                }}
                                            >
                                                <span
                                                    style={{
                                                        fontSize: fluid(20),
                                                        fontWeight: 400,
                                                        color: dayItem.textColor,
                                                    }}
                                                >
                                                    {dayItem.day}
                                                </span>
                                            </div>
                                        );

                                        return dayItem.tooltip ? (
                                            <Tooltip
                                                key={dayIdx}
                                                title={dayItem.tooltip}
                                                getPopupContainer={(triggerNode) => triggerNode?.parentElement || document.body}
                                            >
                                                {cell}
                                            </Tooltip>
                                        ) : (
                                            <React.Fragment key={dayIdx}>
                                                {cell}
                                            </React.Fragment>
                                        );
                                    })}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            <ConfirmLeaveEdit
                visible={confirmVisible}
                onClose={() => setConfirmVisible(false)}
                onDelete={handleConfirmSave}
                deleteLoader={buttonLoader}
                message="Are you sure you want to save the changes?"
            />
        </div>
    );
};

const mapStateToProps = (state) => {
    const { user } = state.session;
    const { holidays } = state.profileNew;
    const { specificUserId } = state.userManagementV2;
    const storeWorkingDaysDetails = state.attendanceNew?.storeWorkingDaysDetails;
    const attendanceRequirement = state.attendanceNew?.getattendanceRequirement;
    return {
        user,
        holidays,
        specificUserId,
        storeWorkingDaysDetails,
        attendanceRequirement,
    };
};

const mapDispatchToProps = (dispatch) => ({
    getHolidays: (params, callback) => dispatch(getHolidays(params, callback)),
    saveHolidays: (params, callback, pagecallback) => dispatch(saveHolidays(params, callback, pagecallback)),
});

export default connect(mapStateToProps, mapDispatchToProps)(HolidaysCalendar);